/**
 * AuthTab Component
 *
 * Authentication testing panel for expiring tokens, testing session flows,
 * and managing authentication state in development.
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
    FiKey,
    FiLogOut
} from 'react-icons/fi';

const AuthTab = ({
    getApiUrl,
    getCookie,
    getUser,
    triggerSessionExpiredModal,
    apiRequest,
    onShowNotification
}) => {
    const expireAccessToken = async () => {
        try {
            const response = await fetch(`${getApiUrl()}/logout`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': getCookie('csrf_access_token')
                },
                body: JSON.stringify({ logout_type: 'access_only' })
            });

            if (response.ok) {
                onShowNotification('Access token expired! Next API call will auto-refresh.');
                console.log('Access token blacklisted, triggering refresh test...');

                setTimeout(async () => {
                    try {
                        await getUser({ forceRefresh: true });
                        console.log('Token was automatically refreshed!');
                    } catch (e) {
                        console.log('Refresh triggered but may have failed:', e);
                    }
                }, 1000);
            }
        } catch (err) {
            onShowNotification('Failed to expire token', 'error');
        }
    };

    const expireRefreshToken = async () => {
        try {
            const response = await fetch(`${getApiUrl()}/logout`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': getCookie('csrf_access_token')
                },
                body: JSON.stringify({ logout_type: 'refresh_only' })
            });

            if (response.ok) {
                onShowNotification('Refresh token expired! Showing session expired modal...');
                setTimeout(() => {
                    triggerSessionExpiredModal();
                }, 500);
            }
        } catch (err) {
            onShowNotification('Failed to expire refresh token', 'error');
        }
    };

    const testSessionModal = () => {
        const event = new CustomEvent('test-session-expired');
        window.dispatchEvent(event);
    };

    const runFullFlowTest = async () => {
        try {
            onShowNotification('Starting full expiration flow test...', 'success');
            console.log('Starting full session expiration flow test...');

            console.log('Step 1: Expiring both access and refresh tokens...');
            const expireResponse = await fetch(`${getApiUrl()}/logout`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': getCookie('csrf_access_token')
                },
                body: JSON.stringify({ logout_type: 'both_tokens' })
            });

            if (expireResponse.ok) {
                console.log('Both tokens expired');

                setTimeout(async () => {
                    console.log('Step 2: Making API call that will get 401...');
                    console.log('This should trigger 401 -> attempt refresh -> refresh fails -> modal shows');

                    try {
                        const response = await apiRequest('/user');

                        if (response.ok) {
                            console.log('API call succeeded (unexpected - tokens should be blacklisted)');
                        } else {
                            console.log('API call failed with status:', response.status);
                            console.log('Session expired modal should now be showing');
                        }
                    } catch (err) {
                        console.log('API call threw error:', err.message);
                        console.log('Session expired modal should now be showing');
                    }
                }, 1000);

                onShowNotification('Tokens expired! Wait for modal to appear...', 'success');
            } else {
                console.error('Failed to expire tokens');
                onShowNotification('Failed to expire tokens', 'error');
            }
        } catch (err) {
            console.error('Test failed:', err);
            onShowNotification('Test failed: ' + err.message, 'error');
        }
    };

    const clearAllTokens = () => {
        document.cookie.split(";").forEach(c => {
            document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
        window.location.reload();
    };

    return (
        <div className="dos-settings-section">
            <div className="dos-section-header">
                <FiKey className="dos-section-icon" />
                <span className="dos-section-title">Authentication Testing</span>
            </div>

            <div className="dos-token-testing-grid">
                {/* Expire Access Token */}
                <div className="dos-test-card">
                    <div className="dos-test-card-content">
                        <h4 className="dos-test-title">Expire Access Token</h4>
                        <p className="dos-test-description">
                            Blacklist current access token to test automatic silent refresh
                        </p>
                    </div>
                    <button
                        className="dos-test-button dos-primary"
                        onClick={expireAccessToken}
                    >
                        Expire Access
                    </button>
                </div>

                {/* Expire Refresh Token */}
                <div className="dos-test-card">
                    <div className="dos-test-card-content">
                        <h4 className="dos-test-title">Expire Refresh Token</h4>
                        <p className="dos-test-description">
                            Blacklist refresh token to test session expired modal
                        </p>
                    </div>
                    <button
                        className="dos-test-button dos-warning"
                        onClick={expireRefreshToken}
                    >
                        Expire Refresh
                    </button>
                </div>

                {/* Test Session Modal */}
                <div className="dos-test-card">
                    <div className="dos-test-card-content">
                        <h4 className="dos-test-title">Test Session Expired Modal</h4>
                        <p className="dos-test-description">
                            Directly trigger session expired modal (UI test only)
                        </p>
                    </div>
                    <button
                        className="dos-test-button dos-secondary"
                        onClick={testSessionModal}
                    >
                        Show Modal
                    </button>
                </div>

                {/* Test Full Expiration Flow */}
                <div className="dos-test-card dos-test-card-full">
                    <div className="dos-test-card-content">
                        <h4 className="dos-test-title">Test Full Expiration Flow</h4>
                        <p className="dos-test-description">
                            Simulates real session expiration: expires both tokens, triggers API call,
                            attempts refresh (which fails), and shows session expired modal
                        </p>
                    </div>
                    <button
                        className="dos-test-button dos-warning-full"
                        onClick={runFullFlowTest}
                    >
                        Run Full Flow Test
                    </button>
                </div>

                {/* Clear All Tokens */}
                <div className="dos-test-card">
                    <div className="dos-test-card-content">
                        <h4 className="dos-test-title">Clear All Tokens</h4>
                        <p className="dos-test-description">
                            Clear all authentication cookies and reload page
                        </p>
                    </div>
                    <button
                        className="dos-test-button dos-danger"
                        onClick={clearAllTokens}
                    >
                        <FiLogOut style={{ marginRight: '6px' }} />
                        Clear Tokens
                    </button>
                </div>
            </div>
        </div>
    );
};

AuthTab.propTypes = {
    getApiUrl: PropTypes.func.isRequired,
    getCookie: PropTypes.func.isRequired,
    getUser: PropTypes.func.isRequired,
    triggerSessionExpiredModal: PropTypes.func.isRequired,
    apiRequest: PropTypes.func.isRequired,
    onShowNotification: PropTypes.func.isRequired
};

export default AuthTab;
